import { allergenMeta } from "@/data/menu";

// Prices stay in the restaurant's currency (TRY for the demo restaurant)
// regardless of UI language.
export function formatTRY(amount) {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
    // Real menus are priced in kuruş (priceMinor); never round 49,90 ₺ to 50 ₺.
    maximumFractionDigits: 2,
  }).format(amount);
}

// Central localization helper for content objects ({ tr, en, ... }).
// Falls back to English, then Turkish, then any value, so missing content
// can never silently render as a blank label.
export function localize(obj, lang) {
  if (obj == null) return "";
  if (typeof obj === "string") return obj;
  const value = obj[lang] ?? obj.en ?? obj.tr ?? Object.values(obj).find((v) => typeof v === "string");
  return value ?? "";
}

// Same fallback rules for localized string lists (e.g. ingredients).
export function localizeList(obj, lang) {
  if (!obj) return [];
  if (Array.isArray(obj)) return obj;
  return obj[lang] ?? obj.en ?? obj.tr ?? [];
}

export function allergenName(id, lang) {
  const meta = allergenMeta[id];
  return meta ? localize(meta.name, lang) : id;
}

// Compute the full price of a configured cart line (base + variant + modifiers).
export function computeLinePrice(product, selections) {
  let unit = product.basePrice;
  (product.modifierGroups || []).forEach((group) => {
    const chosen = selections[group.id] || [];
    group.options.forEach((opt) => {
      if (chosen.includes(opt.id)) unit += opt.priceDelta || 0;
    });
  });
  return unit;
}

// Default selections for a product (defaults / first required option).
export function defaultSelections(product) {
  const sel = {};
  (product.modifierGroups || []).forEach((g) => {
    const def = g.options.find((o) => o.default);
    sel[g.id] = def ? [def.id] : g.required && g.max === 1 ? [g.options[0].id] : [];
  });
  return sel;
}

// Keep only selections that still exist on the current product definition and
// fall back to defaults for required groups that became invalid.
export function sanitizeSelections(product, selections = {}) {
  const defaults = defaultSelections(product);
  const clean = {};
  (product.modifierGroups || []).forEach((g) => {
    const valid = (selections[g.id] || []).filter((id) => g.options.some((o) => o.id === id)).slice(0, g.max);
    clean[g.id] = g.required && valid.length < (g.min || 1) ? defaults[g.id] : valid;
  });
  return clean;
}
